import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Star, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHead, Card } from "@/pages/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const blank = { title: "", description: "", industry: "", tags: "", budget: "", application_deadline: "", proposal_deadline: "", decision_date: "", campaign_date: "", looking_for: "" };
const DATES = [["application_deadline", "Application deadline"], ["proposal_deadline", "Proposal deadline"], ["decision_date", "Decision date"], ["campaign_date", "Campaign date"]];

export default function Network() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState(sp.get("new") === "1");
  const [f, setF] = useState(blank);
  const [meta, setMeta] = useState({ industries: [], budgets: [], company_types: [] });
  const [mine, setMine] = useState([]);
  const [matches, setMatches] = useState([]);
  const [reqs, setReqs] = useState({ incoming: [], outgoing: [] });

  const load = () => {
    api.get("/opportunities").then((r) => setMine((r.data || []).filter((o) => o.company_id === user.company_id))).catch(() => {});
    api.get("/opportunities/matches").then((r) => setMatches(r.data || [])).catch(() => {});
    api.get("/requests").then((r) => setReqs(r.data || { incoming: [], outgoing: [] })).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = (o) => {
    setOpen(o);
    if (!o && sp.get("new")) setSp({});
  };
  const create = async () => {
    try {
      await api.post("/opportunities", { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Opportunity posted — matching companies are being alerted");
      setF(blank);
      close(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const closeOpp = async (id) => {
    await api.patch(`/opportunities/${id}`).catch(() => {});
    load();
  };
  const respond = async (id, status) => {
    await api.patch(`/requests/${id}`, { status }).catch((e) => toast.error(errMsg(e)));
    load();
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div data-testid="network-page">
      <PageHead kicker="Opportunities · requests · matches" title="Network">
        <button className="btn-gold !py-2.5" onClick={() => setOpen(true)} data-testid="post-opportunity-button"><Plus className="h-4 w-4" />Post opportunity</button>
      </PageHead>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6" data-testid="incoming-requests">
          <p className="kicker">Incoming requests</p>
          {!reqs.incoming.length && <p className="mt-4 text-sm text-slate-400">No requests yet.</p>}
          <div className="mt-4 divide-y divide-[#2A1240]/10">
            {reqs.incoming.map((r) => (
              <div key={r.id} className="py-4" data-testid={`incoming-request-${r.id}`}>
                <p className="text-sm text-[#2A1240]"><Link to={`/companies/${r.from_company_id}`} className="hover:underline">{r.from_company_name}</Link> <span className="text-slate-400">· {r.from_user_name}</span></p>
                <p className="mt-1 text-sm text-slate-300">{r.message}</p>
                {r.status === "pending" ? (
                  <div className="mt-3 flex gap-2">
                    <button className="btn-gold !py-1.5 !px-4 !text-xs" onClick={() => respond(r.id, "accepted")} data-testid={`accept-request-${r.id}`}><Check className="h-3.5 w-3.5" />Accept</button>
                    <button className="btn-ghost !py-1.5 !px-4 !text-xs" onClick={() => respond(r.id, "declined")} data-testid={`decline-request-${r.id}`}><X className="h-3.5 w-3.5" />Decline</button>
                  </div>
                ) : <p className="mt-2 text-xs uppercase tracking-wider text-[#8E2BC4]">{r.status}</p>}
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6" data-testid="matched-opportunities">
          <p className="kicker">Matching your profile</p>
          {!matches.length && <p className="mt-4 text-sm text-slate-400">Add partnership interests and industry in your <Link to="/app/profile" className="text-[#8E2BC4] underline">profile</Link>.</p>}
          <div className="mt-4 divide-y divide-[#2A1240]/10">
            {matches.map((o) => (
              <Link key={o.id} to={`/companies/${o.company_id}`} className="block py-4 group">
                <p className="text-sm text-[#2A1240] group-hover:text-[#8E2BC4]">{o.title}</p>
                <p className="text-xs text-slate-400">{o.company_name} · {o.budget} · matched on {o.matched_on.join(", ")}</p>
              </Link>
            ))}
          </div>
        </Card>
        <Card className="p-6 lg:col-span-2" data-testid="my-opportunities">
          <p className="kicker">Your opportunities</p>
          {!mine.length && <p className="mt-4 text-sm text-slate-400">You haven’t posted any opportunities.</p>}
          <div className="mt-4 divide-y divide-[#2A1240]/10">
            {mine.map((o) => (
              <div key={o.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`my-opportunity-${o.id}`}>
                <div>
                  <p className="text-sm text-[#2A1240] flex items-center gap-2">{o.featured && <Star className="h-3.5 w-3.5 text-[#E0218A]" />}{o.title}</p>
                  <p className="text-xs text-slate-400">{(o.tags || []).join(" · ")} {(o.application_deadline || o.deadline) && `· applications close ${fmtDate(o.application_deadline || o.deadline)}`}</p>
                </div>
                <div className="flex gap-2">
                  <button className="btn-ghost !py-1.5 !px-4 !text-xs" onClick={() => closeOpp(o.id)} data-testid={`close-opportunity-${o.id}`}>Close</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6 lg:col-span-2" data-testid="outgoing-requests">
          <p className="kicker">Requests you sent</p>
          {!reqs.outgoing.length && <p className="mt-4 text-sm text-slate-400">None yet — find partners in <Link to="/discover" className="text-[#8E2BC4] underline">Discover</Link>.</p>}
          <div className="mt-4 divide-y divide-[#2A1240]/10">
            {reqs.outgoing.map((r) => (
              <div key={r.id} className="py-3 flex justify-between text-sm"><span>{r.to_company_name}</span><span className="text-xs uppercase tracking-wider text-[#8E2BC4]">{r.status}</span></div>
            ))}
          </div>
        </Card>
      </div>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10 max-w-xl max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="opportunity-dialog">
          <DialogTitle className="font-display text-2xl font-normal">Post an opportunity</DialogTitle>
          <DialogDescription className="text-slate-400">Companies whose interests match your tags get a smart alert instantly.</DialogDescription>
          <input className="field" placeholder="Title (e.g. Title sponsor for our summit)" value={f.title} onChange={set("title")} data-testid="opportunity-title-input" />
          <textarea className="field" rows={3} placeholder="What you're offering and looking for" value={f.description} onChange={set("description")} data-testid="opportunity-description-input" />
          <div className="grid grid-cols-2 gap-3">
            <select className="field" value={f.industry} onChange={set("industry")} data-testid="opportunity-industry-select"><option value="">Industry</option>{meta.industries.map((i) => <option key={i}>{i}</option>)}</select>
            <select className="field" value={f.budget} onChange={set("budget")} data-testid="opportunity-budget-select"><option value="">Budget</option>{meta.budgets.map((i) => <option key={i}>{i}</option>)}</select>
            <select className="field col-span-2" value={f.looking_for} onChange={set("looking_for")} data-testid="opportunity-lookingfor-select"><option value="">Looking for</option>{meta.company_types.map((i) => <option key={i}>{i}</option>)}</select>
            {DATES.map(([k, label]) => (
              <label key={k} className="block text-[11px] uppercase tracking-[0.14em] text-slate-400">{label}
                <input className="field mt-1.5" type="date" value={f[k]} onChange={set(k)} data-testid={`opportunity-${k.replace(/_/g, "-")}-input`} />
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-400">Everyone watching this opportunity gets reminders 14, 7, 5, 3 and 1 day before each date.</p>
          <input className="field" placeholder="Tags, comma separated (Beauty, Creators, Events)" value={f.tags} onChange={set("tags")} data-testid="opportunity-tags-input" />
          <button className="btn-gold" disabled={!f.title} onClick={create} data-testid="opportunity-save-button">Post opportunity</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
